import { z } from 'zod'

/**
 * DTOs compartilhados com o backend (Node/Fastify). Cada schema espelha o shape exato dos
 * endpoints — ver `backend/src/auth/*` e `backend/src/routes/catalogs/*`.
 *
 * Padrão zod: schema definido aqui + tipo derivado via `z.infer`. Validamos toda resposta
 * de rede antes de gravar em Dexie, evitando que mudanças silenciosas no backend corrompam
 * o estado local.
 */

// ============================================================================
//  Auth
// ============================================================================
export const LoginRequestSchema = z.object({
  usuario: z.string().min(1),
  senha: z.string().min(1),
})
export type LoginRequest = z.infer<typeof LoginRequestSchema>

// Role efetiva (derivada no backend a partir da estrutura SIGA):
//  - admin: vê tudo
//  - chefe: vê só obras onde é chefe_administrativo
//  - suporte: espelha o chefe a quem está vinculado (sys_usuario_tem_usuario)
export const UserRoleSchema = z.enum(['admin', 'chefe', 'suporte'])
export type UserRole = z.infer<typeof UserRoleSchema>

const UserSchema = z.object({
  id: z.number(),
  nome: z.string(),
  chapa: z.string().nullable().optional(),
  projetos: z.array(z.number()),
  // role + effectiveChefeId são opcionais pra compat com backends/tokens antigos
  // que não tinham esses campos. Quando ausentes, frontend assume admin (vê tudo).
  role: UserRoleSchema.optional(),
  effectiveChefeId: z.number().nullable().optional(),
})

export const LoginResponseSchema = z.object({
  token: z.string(),
  // Backend não devolve expiresAt — extraímos do payload do JWT (campo `exp`).
  expiresAt: z.string().optional(),
  user: UserSchema,
})
export type LoginResponse = z.infer<typeof LoginResponseSchema>

// Backend devolve `{ user: {...} }`. Achatamos no parse para o consumidor não precisar saber.
export const MeResponseSchema = z
  .object({ user: UserSchema })
  .transform((r) => r.user)
export type MeResponse = z.infer<typeof MeResponseSchema>

// ============================================================================
//  Catalog envelope (todos os GET /catalogs/* devolvem)
// ============================================================================
export const CatalogEnvelopeSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    version: z.string(),
    fetchedAt: z.string(),
    items: z.array(itemSchema),
  })

// ============================================================================
//  Catálogos básicos
// ============================================================================
export const ProjetoDtoSchema = z.object({
  id: z.number(),
  codigo: z.string(),
})
export type ProjetoDto = z.infer<typeof ProjetoDtoSchema>

export const ObraDtoSchema = z.object({
  id: z.number(),
  codigo: z.string(),
  nome: z.string(),
  status: z.string(),
  projeto: z.object({ id: z.number(), codigo: z.string() }).nullable().optional(),
  formulario: z
    .object({ id: z.number(), nome: z.string(), tp: z.string() })
    .nullable()
    .optional(),
  // tipoProjeto vem de cdt_obra_tp_projeto (id=1 Urbana, id=2 Rodoviária).
  // Usado pelo carreteiro pra derivar o tipo da ficha automaticamente.
  tipoProjeto: z
    .object({ id: z.number(), nome: z.string() })
    .nullable()
    .optional(),
  // chefeAdministrativo é o encarregado que vincula MVEs à obra
  // (cdt_obra.obra_chefe_administrativo_id == cdt_mve.mve_chefe_equipe_id).
  chefeAdministrativo: z
    .object({ id: z.number(), nome: z.string() })
    .nullable()
    .optional(),
})
export type ObraDto = z.infer<typeof ObraDtoSchema>

export const NamedDtoSchema = z.object({ id: z.number(), nome: z.string() })
export type NamedDto = z.infer<typeof NamedDtoSchema>

// Para catálogos que passaram a ser escopados por projeto (material rede, diâmetro,
// tipo rede). Quando `projeto_id` é passado na query do backend, este campo vem
// preenchido; sem query, vem null (fetch global — apenas pra outras telas que
// possam consumir, módulo Produção sempre usa por projeto).
export const NamedWithProjetoDtoSchema = z.object({
  id: z.number(),
  nome: z.string(),
  projetoId: z.number().nullable().optional(),
})
export type NamedWithProjetoDto = z.infer<typeof NamedWithProjetoDtoSchema>

export const NamedWithChapaDtoSchema = z.object({
  id: z.number(),
  nome: z.string(),
  chapa: z.string().nullable().optional(),
})
export type NamedWithChapaDto = z.infer<typeof NamedWithChapaDtoSchema>

export const UnidadeDtoSchema = z.object({
  id: z.number(),
  nome: z.string(),
  sigla: z.string().nullable().optional(),
})
export type UnidadeDto = z.infer<typeof UnidadeDtoSchema>

export const MaterialDtoSchema = z.object({
  id: z.number(),
  nome: z.string(),
  unidadeId: z.number().nullable().optional(),
  // Nome da unidade — backend resolve via JOIN com cdt_unidade. Necessário
  // pro app exibir "Material: X (ton)" sem precisar de lookup local.
  unidadeNome: z.string().nullable().optional(),
})
export type MaterialDto = z.infer<typeof MaterialDtoSchema>

export const TrechoDtoSchema = z.object({
  id: z.number(),
  nome: z.string(),
  projetoId: z.number().nullable().optional(),
})
export type TrechoDto = z.infer<typeof TrechoDtoSchema>

export const MveDtoSchema = z.object({
  id: z.number(),
  prefixo: z.string().nullable().optional(),
  // mve_chassi no SIGA — guarda a placa do veículo, normalizada em UPPERCASE pelo backend.
  placa: z.string().nullable().optional(),
  tp: z.string(),
  marca: z.string().nullable().optional(),
  modelo: z.string().nullable().optional(),
  capacidade: z.string().nullable().optional(),
  // FK pra cdt_encarregado. Picker de carreteiro filtra MVEs onde
  // mve.chefeEquipeId == obra.chefeAdministrativoId (filtro local em Dexie).
  chefeEquipeId: z.number().nullable().optional(),
  /**
   * Frota própria ("M+C": mve_tp != 'C' com contratos de locação + produção no
   * mês). As viagens entram na linha da Parte Diária do dia, não numa ficha de
   * mvt_carreteiro — ver CARRETEIRO-PROPRIO-X-TERCEIRO.md.
   */
  proprio: z.boolean().optional(),
})
export type MveDto = z.infer<typeof MveDtoSchema>

export const LocalCargaDtoSchema = z.object({
  id: z.number(),
  origem: z.string().nullable().optional(),
  complemento: z.string().nullable().optional(),
  /** Nome humano pronto (backend compõe origem+complemento). */
  nome: z.string().nullable().optional(),
  projetoId: z.number().nullable().optional(),
})
export type LocalCargaDto = z.infer<typeof LocalCargaDtoSchema>

export const LocalDescargaDtoSchema = z.object({
  id: z.number(),
  // Escopado por projeto — picker do carreteiro filtra por projetoId da ficha.
  projetoId: z.number().nullable().optional(),
  nome: z.string(),
  deposito: z.string().nullable().optional(),
  bairro: z.string().nullable().optional(),
  rua: z.string().nullable().optional(),
})
export type LocalDescargaDto = z.infer<typeof LocalDescargaDtoSchema>

// ============================================================================
//  Serviços com flags dinâmicas (form-driven)
// ============================================================================
export const ServicoFlagsDtoSchema = z.object({
  data: z.boolean(),
  equipe: z.boolean(),
  encarregado: z.boolean(),
  qtdExecutada: z.boolean(),
  qtdMaterial: z.boolean(),
  alturaMedia: z.boolean(),
  materialRede: z.boolean(),
  dn: z.boolean(),
  tpRede: z.boolean(),
  identificador: z.boolean(),
  dtLancamento: z.boolean(),
})
export type ServicoFlagsDto = z.infer<typeof ServicoFlagsDtoSchema>

export const ServicoDtoSchema = z.object({
  id: z.number(),
  nome: z.string(),
  tp: z.string(),
  formulario: z.string().nullable().optional(),
  unidade: UnidadeDtoSchema.nullable().optional(),
  materialIdDefault: z.number().nullable().optional(),
  // material resolvido via JOIN com cdt_material — snapshot pro carreteiro mostrar
  // "Material: Concreto (m³)" read-only após escolher serviço, e decidir se exibe NF/Volume.
  // Produção também consome: quando há material, app exibe read-only com unidade
  // junto e marca qtd material como obrigatória.
  material: z
    .object({
      id: z.number(),
      nome: z.string(),
      unidadeId: z.number().nullable().optional(),
      unidadeNome: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  grupoServicoId: z.number().nullable().optional(),
  // PK do cdt_contrato_item vigente que validou esse serviço pro (MVE, data).
  // Só preenchido quando o endpoint /catalogs/servicos foi chamado com filtro
  // de carreteiro (mve_id + dt). App persiste isso em out_viagem.contratoItemId
  // pra mandar no sync — backend grava em mvt_carreteiro_*.contrato_item_id.
  contratoItemId: z.number().nullable().optional(),
  // Resto do item de contrato (carreteiro) — prévia offline de produção/valor.
  contratoGarantia: z.number().nullable().optional(),
  contratoPreco: z.number().nullable().optional(),
  contratoDtInicio: z.string().nullable().optional(),
  contratoDtFinal: z.string().nullable().optional(),
  contratoItensVigentes: z.number().nullable().optional(),
  inativo: z.boolean().optional(),
  coringa: z.boolean().default(false),
  bloqueioCubagem: z.boolean().default(false),
  isento: z.boolean().default(false),
  flags: ServicoFlagsDtoSchema,
})
export type ServicoDto = z.infer<typeof ServicoDtoSchema>

// ============================================================================
//  Vínculos
// ============================================================================
export const ProjetoServicoDtoSchema = z.object({
  projetoId: z.number(),
  servicoId: z.number(),
  prioridade: z.boolean().default(false),
})
export type ProjetoServicoDto = z.infer<typeof ProjetoServicoDtoSchema>

export const EquipeEncarregadoDtoSchema = z.object({
  projetoId: z.number(),
  equipeId: z.number(),
  equipeNome: z.string(),
  encarregadoId: z.number(),
  encarregadoNome: z.string(),
})
export type EquipeEncarregadoDto = z.infer<typeof EquipeEncarregadoDtoSchema>

// ============================================================================
//  Sync — Produção (POST /sync/producao)
// ============================================================================
export const ProducaoRegistroDtoSchema = z.object({
  id: z.string().uuid(),
  projetoId: z.number().int().positive(),
  servicoExecutadoId: z.number().int().positive(),
  servicoExecutadoNome: z.string().min(1).max(75),
  servicoExecutadoQtd: z.number().nullable().optional(),
  servicoExecutadoPrioridade: z.enum(['N', 'S']).default('N'),
  servicoExecutadoUnidadeId: z.number().int().positive(),
  servicoExecutadoUnidadeNome: z.string().min(1).max(60),
  dt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  equipeId: z.number().int().positive().nullable().optional(),
  equipeNome: z.string().max(30).nullable().optional(),
  encarregadoId: z.number().int().positive().nullable().optional(),
  encarregadoNome: z.string().max(50).nullable().optional(),
  materialId: z.number().int().positive().nullable().optional(),
  materialNome: z.string().max(255).nullable().optional(),
  materialQtd: z.number().nullable().optional(),
  materialUnidadeId: z.number().int().positive().nullable().optional(),
  materialUnidadeNome: z.string().max(60).nullable().optional(),
  alturaMedia: z.number().nullable().optional(),
  materialRedeId: z.number().int().positive().nullable().optional(),
  materialRedeNome: z.string().max(255).nullable().optional(),
  diametroId: z.number().int().positive().nullable().optional(),
  diametroNome: z.string().max(255).nullable().optional(),
  tpRedeId: z.number().int().positive().nullable().optional(),
  tpRedeNome: z.string().max(255).nullable().optional(),
  trechoId: z.number().int().positive().nullable().optional(),
  trechoNome: z.string().max(255).nullable().optional(),
  estaca: z.string().max(255).nullable().optional(),
  estacaInicial: z.string().max(255).nullable().optional(),
  estacaFinal: z.string().max(255).nullable().optional(),
  chuva: z.enum(['N', 'S']).default('N'),
  obs: z.string().nullable().optional(),
  capturedAt: z.string().datetime({ offset: true }),
  deletado: z.enum(['N', 'S']).default('N'),
})
export type ProducaoRegistroDto = z.infer<typeof ProducaoRegistroDtoSchema>

export const ProducaoBatchResponseSchema = z.object({
  accepted: z.array(z.object({ id: z.string(), syncedAt: z.string() })),
  rejected: z.array(
    z.object({
      id: z.string(),
      reason: z.string(),
      details: z.unknown().optional(),
    }),
  ),
})
export type ProducaoBatchResponse = z.infer<typeof ProducaoBatchResponseSchema>

// ============================================================================
//  Sync — Carreteiro (POST /sync/carreteiro) — ficha + viagens em uma chamada
// ============================================================================
export const POSICOES_ROD = [
  'LD', 'LE', 'PD-LD', 'PD-LE',
  '3-FX-LD', '3-FX-LE',
  'AC-PD-LD', 'AC-PD-LE', 'AC-LD', 'AC-LE',
] as const
export const POSICOES_URB = ['LD', 'LE', 'PD-LD', 'PD-LE'] as const

const ViagemDtoBaseSchema = z.object({
  id: z.string().uuid(),
  servicoExecutadoId: z.number().int().positive(),
  servicoExecutadoNome: z.string().min(1).max(75),
  contratoItemId: z.number().int().positive().nullable().optional(),
  mveId: z.number().int().positive(),
  // Material derivado do serviço escolhido (snapshot). Backend re-deriva concreto
  // de materialNome — então mesmo se app enviar errado, banco fica consistente.
  materialId: z.number().int().positive().nullable().optional(),
  materialNome: z.string().max(255).nullable().optional(),
  localCargaId: z.number().int().positive().nullable().optional(),
  localCargaNome: z.string().max(255).nullable().optional(),
  cargaChapaApontador: z.string().max(6).nullable().optional(),
  cargaHora: z.string().max(8).nullable().optional(),
  cargaEstacaOrigem: z.string().max(6).nullable().optional(),
  // DT fixo (km) — entrada do operador. Backend calcula
  // dtcalc/dtpg/producao/vpgcar baseado nele + estacas (rodoviário).
  cargaDtFixo: z.number().nullable().optional(),
  localDescargaId: z.number().int().positive().nullable().optional(),
  localDescargaNome: z.string().max(255).nullable().optional(),
  descargaChapaApontador: z.string().max(6).nullable().optional(),
  descargaHora: z.string().max(8).nullable().optional(),
  descargaLargura: z.number().nullable().optional(),
  descargaComprimento: z.number().nullable().optional(),
  qdeVg: z.number().int().nullable().optional(),
  producao: z.number().nullable().optional(),
  concreto: z.enum(['N', 'S']).nullable().optional(),
  nf: z.string().max(10).nullable().optional(),
  volume: z.number().nullable().optional(),
  deletado: z.enum(['N', 'S']).default('N'),
})

// Um só schema para R e U (espelha o backend): o tipo da ficha decide quais
// campos valem e a normalização zera o resto.
export const ViagemCarreteiroDtoSchema = ViagemDtoBaseSchema.extend({
  descargaEstacaDescarga: z.string().max(6).nullable().optional(),
  descargaBairro: z.string().max(50).nullable().optional(),
  descargaRua: z.string().max(50).nullable().optional(),
  descargaPosicao: z.enum(POSICOES_ROD).nullable().optional(),
})

export const FichaCarreteiroDtoSchema = z.object({
  id: z.string().uuid(),
  tipo: z.enum(['R', 'U']),
  mveId: z.number().int().positive(),
  obraId: z.number().int().positive(),
  // Informativos: o servidor recalcula o chefe pelo histórico do projeto na data.
  chefeEquipeId: z.number().int().positive().nullable().optional(),
  chefeEquipeNome: z.string().max(100).nullable().optional(),
  cadastradorId: z.number().int().positive().nullable().optional(),
  dtMovimentacao: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  // ficha + chapaMotorista: removidos do app (decisão 2026-05-25), mantidos opcionais
  // só pra compat com outboxes antigos.
  ficha: z.string().max(50).nullable().optional(),
  kmInicial: z.number().int().nullable().optional(),
  kmFinal: z.number().int().nullable().optional(),
  chapaEncarregado: z.string().max(11).nullable().optional(),
  chapaMotorista: z.string().max(11).nullable().optional(),
  // Flag da ficha: todas as viagens com NF (S) ou todas sem (N).
  fichaConcreto: z.enum(['N', 'S']).optional(),
  capturedAt: z.string().datetime({ offset: true }),
  deletado: z.enum(['N', 'S']).default('N'),
  viagens: z.array(ViagemCarreteiroDtoSchema).default([]),
})
export type FichaCarreteiroDto = z.infer<typeof FichaCarreteiroDtoSchema>
export type ViagemCarreteiroDto = z.infer<typeof ViagemCarreteiroDtoSchema>

/** GET /catalogs/chefe-obra — chefe do projeto da obra na data (null = sem chefe). */
export const ChefeObraResponseSchema = z.object({
  dt: z.string(),
  chefe: z.object({ id: z.number(), nome: z.string() }).nullable(),
  message: z.string().nullable(),
})
export type ChefeObraResponse = z.infer<typeof ChefeObraResponseSchema>

/** GET /carreteiro/precheck — regras de cabeçalho do sync, sem gravar. */
export const PrecheckCarreteiroResponseSchema = z.object({
  ok: z.boolean(),
  chefe: z.object({ id: z.number(), nome: z.string() }).nullable(),
  transferenciaId: z.number().nullable(),
  bloqueios: z.array(z.object({ code: z.string(), message: z.string() })),
  avisos: z.array(z.string()),
})
export type PrecheckCarreteiroResponse = z.infer<typeof PrecheckCarreteiroResponseSchema>

// Backend devolve ok+carreteiroId após insert transacional. Aceitamos ambos os
// formatos por defesa (resposta antiga era { accepted, rejected }).
/** Resposta do envio de ficha de FROTA PRÓPRIA: anexada à linha do dia, ou guardada no servidor à espera dela. */
export const CarreteiroProprioSyncResponseSchema = z
  .object({
    ok: z.literal(true),
    proprio: z.literal(true),
    appUuid: z.string(),
    aguardandoParteDiaria: z.boolean().optional(),
    motivo: z.string().nullable().optional(),
    jaGravado: z.boolean().optional(),
    sigaMveId: z.number().optional(),
    viagensInseridas: z.number(),
    syncedAt: z.string(),
  })
  .passthrough()

export const CarreteiroSyncResponseSchema = z.union([
  CarreteiroProprioSyncResponseSchema,
  z.object({
    ok: z.literal(true),
    appUuid: z.string(),
    carreteiroId: z.number(),
    viagensInseridas: z.number(),
    tipo: z.enum(['R', 'U']),
    fichaConcreto: z.enum(['N', 'S']),
    numeroFicha: z.string().optional(),
    valorFicha: z.number().optional(),
    syncedAt: z.string(),
  }),
  z.object({
    accepted: z.object({
      fichaId: z.string(),
      viagens: z.array(z.string()),
      syncedAt: z.string(),
    }),
    rejected: z.array(z.unknown()).default([]),
  }),
])
export type CarreteiroSyncResponse = z.infer<typeof CarreteiroSyncResponseSchema>

// ============================================================================
//  Sync — Fotos (POST /sync/fotos multipart)
// ============================================================================
export const FotoMetadataDtoSchema = z.object({
  id: z.string().uuid(),
  controleProducaoId: z.string().uuid().nullable().optional(),
  carreteiroId: z.string().uuid().nullable().optional(),
  projetoId: z.number().int().positive(),
  projetoCodigo: z.string().min(1).max(25),
  servicoExecutadoId: z.number().int().positive().nullable().optional(),
  servicoExecutadoNome: z.string().max(75).nullable().optional(),
  encarregadoId: z.number().int().positive().nullable().optional(),
  encarregadoNome: z.string().max(50).nullable().optional(),
  equipeId: z.number().int().positive().nullable().optional(),
  equipeNome: z.string().max(30).nullable().optional(),
  lat: z.number().min(-90).max(90).nullable().optional(),
  lng: z.number().min(-180).max(180).nullable().optional(),
  accuracy: z.number().nonnegative().nullable().optional(),
  capturedAt: z.string().datetime({ offset: true }),
  mime: z.string().regex(/^image\//),
  sizeBytes: z.number().int().nonnegative(),
  width: z.number().int().positive().nullable().optional(),
  height: z.number().int().positive().nullable().optional(),
  sha256: z.string().length(64).optional(),
  obs: z.string().nullable().optional(),
})
export type FotoMetadataDto = z.infer<typeof FotoMetadataDtoSchema>

export const FotoSyncResponseSchema = z.object({
  id: z.string(),
  // Path do objeto no storage (Supabase). Renomeado de `minioKey`.
  storageKey: z.string(),
  uploadedAt: z.string().nullable(),
  presignedGet: z.string().nullable(),
  idempotent: z.boolean(),
  // Flag adicionada na sanitização 2026-05-24: backend confirma presença real
  // do blob no bucket (caminho normal OU idempotente com headObject ok). Cliente
  // deve marcar SYNCED somente se true. Opcional pra backwards compat — ausente
  // = backend antigo, assumir não verificado.
  blobVerified: z.boolean().optional(),
})
export type FotoSyncResponse = z.infer<typeof FotoSyncResponseSchema>

/**
 * Resposta do GET /sync/fotos/:appUuid/signed-url.
 * Usado pelo frontend quando o Blob local foi evictado pelo Safari — pega a foto
 * de volta direto do Supabase Storage via URL assinada.
 */
export const FotoSignedUrlResponseSchema = z.object({
  appUuid: z.string(),
  storageKey: z.string(),
  signedUrl: z.string(),
  ttlSeconds: z.number().int().positive(),
})
export type FotoSignedUrlResponse = z.infer<typeof FotoSignedUrlResponseSchema>
